"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Ban, CircleCheck as CheckCircle2, Clock, Droplets, PhoneCall, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const drainIssues = [{
  id: "slow-drains",
  title: "Slow Draining Sinks & Tubs",
  badge: "Early Warning",
  description: "Water pooling around your feet in the shower or taking minutes to clear the kitchen sink indicates soap scum, hair, and grease buildup inside the line.",
  symptom: "Standing water in showers, sinks, or laundry basins",
  solution: "Motorized snaking and pipe de-scaling to restore full flow.",
  iconName: "Clock"
}, {
  id: "recurring-clogs",
  title: "Recurring & Stubborn Clogs",
  badge: "Frequent Issue",
  description: "Plunging only gives temporary relief because the main blockage sits deeper in the branch line or main sewer stack.",
  symptom: "Clogs returning days after manual clearing",
  solution: "Hydro-jetting and motorized cable cleaning to clear pipe walls.",
  iconName: "Ban"
}, {
  id: "multiple-backups",
  title: "Multiple Fixture Backups",
  badge: "Urgent Problem",
  description: "When running your washing machine causes toilets to bubble or bathtubs to fill with wastewater, the main building sewer line is obstructed.",
  symptom: "Water backing up across multiple rooms simultaneously",
  solution: "High-power main line clearing and camera inspection.",
  iconName: "Waves"
}, {
  id: "foul-odors",
  title: "Persistent Foul Odors",
  badge: "Health Hazard",
  description: "Nasty sewer gas smells or rotting food odors coming from drains mean trapped waste or a compromised dry P-trap venting into your living space.",
  symptom: "Rotten egg or decaying grease smell from drain openings",
  solution: "Deep line sanitizing, vent line checks, and organic clearing.",
  iconName: "ShieldAlert"
}, {
  id: "gurgling-pipes",
  title: "Gurgling & Bubbling Noises",
  badge: "Airflow Block",
  description: "Strange bubbling sounds occur when trapped air cannot vent freely through pipes, forced back up past drain water seals by downstream clogs.",
  symptom: "Toilets gurgling when sinks or tubs drain",
  solution: "Sewer obstruction clearing and vent stack inspection.",
  iconName: "Droplets"
}, {
  id: "sewage-overflow",
  title: "Emergency Sewage Overflow",
  badge: "24/7 Emergency",
  description: "Raw wastewater rising into basement drains, showers, or lower-level fixtures creates immediate property damage and health risks requiring fast response.",
  symptom: "Dirty blackwater pushing up through floor drains or showers",
  solution: "Immediate 24/7 emergency dispatch and line extraction.",
  iconName: "AlertTriangle"
}];
export default function DrainProblems() {
  const shouldReduceMotion = useReducedMotion();
  const getProblemIcon = (iconName: string) => {
    switch (iconName) {
      case "Clock":
        return <Clock className="size-6 text-primary" />;
      case "Ban":
        return <Ban className="size-6 text-primary" />;
      case "Waves":
        return <Waves className="size-6 text-primary" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-primary" />;
      case "Droplets":
        return <Droplets className="size-6 text-primary" />;
      case "AlertTriangle":
        return <AlertTriangle className="size-6 text-destructive" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="commondrainproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Diagnostic Guide
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Common Drain Problems We Fix Fast
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Drain troubles start small and escalate into expensive emergency water damage. If you notice any of these signs in your Jonesboro home or business, our 24/7 plumbers have the heavy-duty equipment to clear it permanently.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {drainIssues.map((issue, index) => <motion.div key={issue.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="flex h-full">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="gap-3 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                      {getProblemIcon(issue.iconName)}
                    </div>
                    <Badge variant={issue.badge === "24/7 Emergency" ? "destructive" : "outline"} className="text-xs font-medium">
                      {issue.badge}
                    </Badge>
                  </div>
                  <CardTitle className="font-default text-xl font-bold text-card-foreground">
                    {issue.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {issue.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-2 flex flex-col gap-3">
                  <div className="p-3 rounded-md bg-muted/60 border border-border/60">
                    <span className="text-xs font-semibold text-muted-foreground block mb-1">
                      Key Symptom:
                    </span>
                    <p className="text-xs text-foreground font-medium">
                      {issue.symptom}
                    </p>
                  </div>

                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">Our Fix: </strong>
                      {issue.solution}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Call to Action Card */}
        <div className="mt-12 md:mt-16 p-6 sm:p-8 lg:p-10 rounded-2xl bg-secondary border border-border flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center lg:text-left">
            <span className="text-xs font-bold uppercase tracking-wider text-secondary-foreground block mb-1">
              Jonesboro Emergency Drain Team
            </span>
            <h3 className="font-default text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Facing an active clog or sewage backup right now?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Do not wait for wastewater to damage your floors. We provide rapid dispatch 24 hours a day with upfront pricing.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0 justify-center">
            <Button size="lg" asChild className="w-full sm:w-auto shadow-sm">
              <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                Call (470) 847-9076
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                Schedule Service
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}